import type { ComponentPropsWithRef } from 'react';

import { cn } from '../../lib/cn';

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerTone = 'current' | 'primary' | 'accent' | 'muted' | 'inverse';

/**
 * Ring weights come off the border scale, not off Tailwind's: `sm`/`md` keep the
 * 2px weight — the thinnest that reads at a 16–20px box, below the scale's
 * smallest step — and `lg`/`xl` take `--border-w-strong` (3px). `xl` was
 * `border-4`, a fourth weight that existed nowhere else in the system.
 */
const SIZES: Record<SpinnerSize, string> = {
  sm: 'size-4 border-2',
  md: 'size-5 border-2',
  lg: 'size-7 border-[length:var(--border-w-strong)]',
  xl: 'size-10 border-[length:var(--border-w-strong)]',
};

const TONES: Record<SpinnerTone, string> = {
  current: 'border-current border-t-transparent',
  primary: 'border-primary border-t-transparent',
  accent: 'border-accent border-t-transparent',
  muted: 'border-line border-t-transparent',
  inverse: 'border-inverse border-t-transparent',
};

export interface SpinnerProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  size?: SpinnerSize;
  tone?: SpinnerTone;
  /** Accessible name. Omit when the spinner sits inside a control that already
   *  names itself (a loading button), so screen readers do not hear it twice. */
  label?: string;
}

/**
 * Indeterminate loading indicator.
 *
 * The rotation is the loading state itself, not decoration, so it runs whenever
 * the spinner is present — the animation level never removes it. Under
 * `prefers-reduced-motion` the spin stops (global rule) and the ring is left as
 * a static partial arc, which still reads as "working" rather than as an icon.
 */
export function Spinner({ size = 'md', tone = 'current', label, className, ...rest }: SpinnerProps) {
  return (
    <span
      {...rest}
      role="status"
      aria-label={label}
      aria-live={label ? 'polite' : undefined}
      className={cn(
        // `rounded-pill` (999px), the radius token for a circle — `rounded-full`
        // is Tailwind's own 9999px and existed nowhere else in the library.
        'inline-block shrink-0 animate-[dhd-spin_0.7s_linear_infinite] rounded-pill',
        SIZES[size],
        TONES[tone],
        className,
      )}
    />
  );
}
